import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CartService } from '../../service/manage/exam_subscription/cart.service';

/**
 * Cart Context
 * Manages temporary cart state before checkout
 * This simulates session-based cart that would normally be managed by backend
 */

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    // Temporary cart state - cleared after checkout
    const [selectedCreditPapers, setSelectedCreditPapers] = useState([]);
    const [selectedPurchasePapers, setSelectedPurchasePapers] = useState([]);
    const [selectedCreditPackages, setSelectedCreditPackages] = useState([]);
    const [purchaseType, setPurchaseType] = useState(null); // 'credits' | 'cash'
    const [cartItemCount, setCartItemCount] = useState(0);

    const fetchCartItemCount = useCallback(async () => {
        const userId = sessionStorage.getItem('manage_user_id');
        if (!userId) return;
        try {
            const response = await CartService.getCartSummary(userId);
            if (response.success) {
                setCartItemCount(response.data?.itemCount ?? 0);
            }
        } catch (error) {
            console.error('Error fetching cart item count:', error);
        }
    }, []);

    useEffect(() => {
        fetchCartItemCount();

        const handleCartUpdate = () => fetchCartItemCount();
        window.addEventListener('cart-updated', handleCartUpdate);
        window.addEventListener('user-logged-in', handleCartUpdate);
        return () => {
            window.removeEventListener('cart-updated', handleCartUpdate);
            window.removeEventListener('user-logged-in', handleCartUpdate);
        };
    }, [fetchCartItemCount]);

    /**
     * Mock API: Validate cart before checkout
     * Simulates backend validation endpoint
     * TODO: Replace with axios.post('/api/cart/validate', { items, type })
     */
    const validateCart = async (items, packages, type) => {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 300));

        try {
            // Simulate backend validation logic
            if (type === 'credits') {
                // Validate credit-based purchases
                const validatedItems = items.map(item => ({
                    ...item,
                    status: 'valid',
                    credits_required: item.credits_required,
                }));

                const totalCredits = validatedItems.reduce(
                    (sum, item) => sum + item.credits_required,
                    0
                );

                return {
                    success: true,
                    items: validatedItems,
                    summary: {
                        totalCredits,
                        itemCount: validatedItems.length,
                    },
                    message: 'Cart validated successfully'
                };
            } else {
                // Validate cash-based purchases
                const validatedPapers = items.map(item => ({
                    ...item,
                    status: 'valid',
                    final_price: item.discount_price || item.price,
                }));

                const validatedPackages = packages.map(pkg => ({
                    ...pkg,
                    status: 'valid',
                    final_price: pkg.discount_price || pkg.price,
                }));

                const papersTotal = validatedPapers.reduce(
                    (sum, item) => sum + item.final_price,
                    0
                );
                const packagesTotal = validatedPackages.reduce(
                    (sum, pkg) => sum + pkg.final_price,
                    0
                );
                const discount = items.reduce((sum, item) => {
                    if (item.discount_price && item.price) {
                        return sum + (item.price - item.discount_price);
                    }
                    return sum;
                }, 0) + packages.reduce((sum, pkg) => {
                    if (pkg.discount_price && pkg.price) {
                        return sum + (pkg.price - pkg.discount_price);
                    }
                    return sum;
                }, 0);

                return {
                    success: true,
                    papers: validatedPapers,
                    packages: validatedPackages,
                    summary: {
                        papersTotal,
                        packagesTotal,
                        discount,
                        totalAmount: papersTotal + packagesTotal,
                        itemCount: validatedPapers.length + validatedPackages.length,
                    },
                    message: 'Cart validated successfully'
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Cart validation failed',
                error: error.message
            };
        }
    };

    /**
     * Mock API: Create payment order
     * Simulates backend order creation for cash purchases
     * TODO: Replace with axios.post('/api/orders/create', { items, packages, total })
     */
    const createOrder = async (items, packages, totalAmount) => {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            // Simulate backend order creation
            const orderId = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const sessionId = `SESSION-${Date.now()}`;

            return {
                success: true,
                order: {
                    orderId,
                    sessionId,
                    amount: totalAmount,
                    currency: 'LKR',
                    items: items.length,
                    packages: packages.length,
                    status: 'pending',
                    createdAt: new Date().toISOString(),
                },
                paymentUrl: `/payment-gateway?session=${sessionId}&order=${orderId}`,
                message: 'Order created successfully'
            };
        } catch (error) {
            return {
                success: false,
                message: 'Order creation failed',
                error: error.message
            };
        }
    };

    /**
     * Mock API: Enroll with credits
     * Simulates backend enrollment using user credits
     * TODO: Replace with axios.post('/api/enrollment/credits', { items })
     */
    const enrollWithCredits = async (items, totalCredits) => {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            // Simulate backend enrollment logic
            const enrollmentId = `ENR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // Simulate credit deduction (would be done on backend)
            const enrolledItems = items.map(item => ({
                ...item,
                enrollmentStatus: 'enrolled',
                enrolledAt: new Date().toISOString(),
            }));

            return {
                success: true,
                enrollment: {
                    enrollmentId,
                    itemsEnrolled: enrolledItems,
                    creditsDeducted: totalCredits,
                    remainingCredits: 100 - totalCredits, // Mock remaining credits
                    status: 'completed',
                    createdAt: new Date().toISOString(),
                },
                message: 'Enrollment successful'
            };
        } catch (error) {
            return {
                success: false,
                message: 'Enrollment failed',
                error: error.message
            };
        }
    };

    /**
     * Set cart items for checkout
     */
    const setCartForCheckout = (creditPapers = [], purchasePapers = [], packages = [], type) => {
        setSelectedCreditPapers(creditPapers);
        setSelectedPurchasePapers(purchasePapers);
        setSelectedCreditPackages(packages);
        setPurchaseType(type);
    };

    /**
     * Clear cart after successful checkout
     */
    const clearCart = () => {
        setSelectedCreditPapers([]);
        setSelectedPurchasePapers([]);
        setSelectedCreditPackages([]);
        setPurchaseType(null);
    };

    const value = {
        // State
        selectedCreditPapers,
        selectedPurchasePapers,
        selectedCreditPackages,
        purchaseType,
        cartItemCount,

        // Actions
        setCartForCheckout,
        clearCart,
        fetchCartItemCount,

        // Mock API calls
        validateCart,
        createOrder,
        enrollWithCredits,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
